import React, { useContext, useState } from 'react';
import { ExperienceService } from 'core/api/services/experience';
import { CostService } from 'core/api/services/cost';

const ExperienceContext = React.createContext(null);

export const ExperienceProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [loadingSchedule, setLoadingSchedule] = useState(false);
    const [experiences, setExperiences] = useState([]);
    const [experience, setExperience] = useState([]);
    const [experienceDetails, setExperienceDetails] = useState([]);
    const [experienceSchedule, setExperienceSchedule] = useState([]);
    const [total, setTotal] = useState([]);
    const [costs, setCosts] = useState([]);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const getExperiences = async (options) => {
        try {
            setLoading(true);
            const result = await ExperienceService.getExperiences(options);
            if (!result) {
                throw new Error();
            }
            setExperiences(result.experiences);
            setTotal(result.total);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getExperience = async (id) => {
        try {
            setLoading(true);
            const result = await ExperienceService.get(id);
            if (!result) {
                throw new Error();
            }
            setExperience(result);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };
    const updateExperience = async (values) => {
        try {
            setLoading(true);
            const result = await ExperienceService.update(values);
            if (!result) {
                throw new Error();
            }
            return result;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const getExperienceSchedule = async (range) => {
        try {
            if (!experience.id) {
                return;
            }
            setLoadingSchedule(true);
            const result = await ExperienceService.getExperienceSchedule({ id: experience.id, range });
            if (!result) {
                throw new Error();
            }
            setExperienceSchedule(result);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoadingSchedule(false);
        }
    };
    const addExperienceSchedule = async (values) => {
        try {
            setLoadingSchedule(true);
            const result = await ExperienceService.addExperienceSchedule(values);
            if (!result) {
                throw new Error();
            }
            return result;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoadingSchedule(false);
        }
    };
    const updateExperienceSchedule = async (values) => {
        try {
            setLoadingSchedule(true);
            const result = await ExperienceService.updateExperienceSchedule(values);
            if (!result) {
                throw new Error();
            }
            return result;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoadingSchedule(false);
        }
    };

    const deleteExperienceSchedule = async (values) => {
        try {
            setLoadingSchedule(true);
            const result = await ExperienceService.deleteExperienceSchedule(values);
            if (!result) {
                throw new Error();
            }
            return result;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoadingSchedule(false);
        }
    };

    // Costs
    const getCosts = async (experienceId) => {
        try {
            setLoading(true);
            const result = await CostService.getByEntity('experience', experienceId);

            if (!result) {
                throw new Error();
            }

            setCosts(result);

            return true;
        } catch (error) {
            console.error(error);
            setCosts([]);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const postCost = async (values) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.postCostByEntity('experience', values);

            if (!result) {
                throw new Error();
            }

            return result;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const patchCost = async (costId, values) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.updateCostByEntity('experience', costId, values);

            if (!result) {
                throw new Error();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const deleteCost = async (costId) => {
        try {
            setLoadingSubmit(true);
            const result = await CostService.deleteCostByEntity('experience', costId);

            if (!result) {
                throw new Error();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        } finally {
            setLoadingSubmit(false);
        }
    };

    const getExperienceDetails = async (id) => {
        try {
            setLoading(true);
            const result = await ExperienceService.getExpDetails(id);
            if (!result) {
                throw new Error('Failed to get experience details');
            }
            setExperienceDetails(result);
            return true;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    const updateExperienceDetail = async (values) => {
        try {
            setLoading(true);
            const result = await ExperienceService.updateExpDetail(values);
            if (!result) {
                throw new Error('Failed to update experience detail');
            }
            return result;
        } catch (err) {
            console.error(err);
            return false;
        } finally {
            setLoading(false);
        }
    };

    return (
        <ExperienceContext.Provider
            value={{
                loading,
                loadingSchedule,
                experiences,
                total,
                getExperiences,
                experience,
                experienceDetails,
                getExperienceDetails,
                updateExperienceDetail,
                setExperience,
                getExperience,
                updateExperience,
                experienceSchedule,
                setExperienceSchedule,
                getExperienceSchedule,
                addExperienceSchedule,
                updateExperienceSchedule,
                deleteExperienceSchedule,

                // Costs
                getCosts,
                postCost,
                patchCost,
                deleteCost,
                costs,
                loadingSubmit,
            }}>
            {children}
        </ExperienceContext.Provider>
    );
};

ExperienceProvider.displayName = 'ExperienceProvider';

export const useExperience = () => useContext(ExperienceContext);
